import React, { useEffect, useState } from "react";
import { Box, Stack, TextField, Typography, Button, IconButton } from "@mui/material";
import {
    Person,
    Business,
    Email,
    LocationOn,
    Badge,
    CloudUpload,
    Delete as DeleteIcon,
    PhotoCamera
} from "@mui/icons-material";
import { output } from "../../settings/themeSettings";
import { createClient, editClient, fetchClient } from "../../data/slices/clientStore";
import { notify } from "../../settings/toastSetting";

const ClientForm = ({ id = null, onSuccess, onCancel }) => {
    const [clientType, setClientType] = useState("personne");
    const [client, setClient] = useState({ 
        nom: '', prenom: '', structure: false, code: '', email: '', 
        telephone: '', pays: 'Sénégal', ville: 'Dakar', adresse: '', logo: null, 
        encoursMaximum: 0, miseEnSommeil: false, contact: '', balance: 0 
    });
    const [loading, setLoading] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);

    const compressImage = (file, maxWidth = 800, quality = 0.7) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                    resolve(compressedBase64);
                };
                img.onerror = reject;
            };
            reader.onerror = reject;
        });
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                setLoading(true);
                const compressedLogo = await compressImage(file, 800, 0.7);
                setLogoPreview(compressedLogo);
                setClient({ ...client, logo: compressedLogo });
                setLoading(false);
            } catch (error) {
                console.error('Erreur compression image:', error);
                notify("Erreur lors du traitement de l'image", "error");
                setLoading(false);
            }
        }
    };

    const removeLogo = () => {
        setLogoPreview(null);
        setClient({ ...client, logo: null });
    };

    const handleSubmit = () => {
        if (!client.nom || !client.code) {
            notify("Le nom et le code sont obligatoires !", "warning");
            return;
        }
        if (!client.pays) {
            notify("Le pays est obligatoire !", "warning");
            return;
        }

        setLoading(true);

        // Nettoyage de l'objet : on exclut la méthode hasError() pour ne pas corrompre le JSON
        const { hasError, ...clientData } = client;

        console.log('[DEBUG] Données envoyées à createClient:', clientData);

        const successCallback = (resp) => {
            notify(id ? "Client mis à jour avec succès !" : "Nouveau client enregistré !", "success");
            setLoading(false);
            if (onSuccess) onSuccess(resp);
        };

        const errorCallback = (err) => {
            notify("Erreur lors de l'enregistrement. Vérifiez les champs.", "error");
            setLoading(false);
        };

        if (id) {
            editClient(id, clientData, errorCallback, successCallback);
        } else {
            createClient(clientData, errorCallback, successCallback);
        }
    }

    useEffect(() => {
        if (id) {
            fetchClient(id, (err) => output(err), (clientData) => {
                setClient(clientData);
                if (clientData.structure) setClientType("structure");
                if (clientData.logo) setLogoPreview(clientData.logo);
            });
        }
    }, [id]);

    useEffect(() => {
        setClient(prev => ({ ...prev, structure: (clientType === "structure") }))
    }, [clientType]);

    return (
        <Box className="client-form-cyber">
            {/* Toggle Type Client */}
            <Box className="cyber-tabs">
                <Box
                    className={`cyber-tab ${clientType === "personne" ? "active" : ""}`}
                    onClick={() => setClientType("personne")}
                >
                    <Person sx={{ mr: 1, fontSize: '1.2rem' }} /> Personne Physique
                </Box>
                <Box
                    className={`cyber-tab ${clientType === "structure" ? "active" : ""}`}
                    onClick={() => setClientType("structure")}
                >
                    <Business sx={{ mr: 1, fontSize: '1.2rem' }} /> Personne Morale (Structure)
                </Box>
            </Box>

            <Box className="form-sections-grid">
                {/* Section IDENTITÉ */}
                <Box className="form-section">
                    <Typography className="section-title-cyber">
                        <Badge /> Informations d'Identité
                    </Typography>

                    <TextField
                        className="cyber-input-field"
                        label={clientType === "personne" ? "Nom de famille" : "Nom de la structure"}
                        placeholder={clientType === "personne" ? "Ex: Dupont" : "Ex: SARL AFT"}
                        fullWidth
                        required
                        value={client.nom || ''}
                        onChange={(e) => setClient({ ...client, nom: e.target.value.toUpperCase() })}
                    />

                    {clientType === "personne" && (
                        <TextField
                            className="cyber-input-field"
                            label="Prénom(s)"
                            placeholder="Ex: Jean-Marc"
                            fullWidth
                            value={client.prenom || ''}
                            onChange={(e) => setClient({ ...client, prenom: e.target.value })}
                        />
                    )}

                    <TextField
                        className="cyber-input-field"
                        label="Code Client (Alphanumérique)"
                        placeholder="Ex: DUPONT001"
                        fullWidth
                        required
                        value={client.code || ''}
                        onChange={(e) => {
                            const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                            setClient({ ...client, code: val });
                        }}
                        helperText="Saisissez un code unique composé de lettres et chiffres"
                    />
                </Box>

                {/* Section CONTACT */}
                <Box className="form-section">
                    <Typography className="section-title-cyber">
                        <Email /> Coordonnées & Contact
                    </Typography>

                    <TextField
                        className="cyber-input-field"
                        label="Adresse Email professionnelle"
                        placeholder="contact@exemple.com"
                        fullWidth
                        type="email"
                        value={client.email || ''}
                        onChange={(e) => setClient({ ...client, email: e.target.value.toLowerCase() })}
                    />

                    <TextField
                        className="cyber-input-field"
                        label="Numéro de Téléphone"
                        placeholder="+221 ..."
                        fullWidth
                        value={client.telephone || ''}
                        onChange={(e) => setClient({ ...client, telephone: e.target.value })}
                    />

                    <TextField
                        className="cyber-input-field"
                        label="Numéro de Compte / Ref"
                        placeholder="ID Comptable"
                        fullWidth
                        value={client.compte || ''}
                        onChange={(e) => setClient({ ...client, compte: e.target.value })}
                    />
                </Box>

                {/* Section LOCALISATION */}
                <Box className="form-section">
                    <Typography className="section-title-cyber">
                        <LocationOn /> Localisation Géo
                    </Typography>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            className="cyber-input-field"
                            label="Pays"
                            placeholder="Sénégal"
                            fullWidth
                            value={client.pays || ''}
                            onChange={(e) => setClient({ ...client, pays: e.target.value })}
                        />
                        <TextField
                            className="cyber-input-field"
                            label="Ville"
                            placeholder="Dakar"
                            fullWidth
                            value={client.ville || ''}
                            onChange={(e) => setClient({ ...client, ville: e.target.value })}
                        />
                    </Stack>

                    <TextField
                        className="cyber-input-field"
                        label="Adresse Physique Complète"
                        placeholder="Rue, Immeuble, Appartement..."
                        fullWidth
                        multiline
                        rows={2}
                        value={client.adresse || ''}
                        onChange={(e) => setClient({ ...client, adresse: e.target.value })}
                    />
                </Box>

                {/* Section LOGO / IMAGE */}
                <Box className="logo-upload-section">
                    <Box className="logo-preview">
                        {logoPreview ? (
                            <img src={logoPreview} alt="Logo preview" />
                        ) : (
                            <Business />
                        )}
                    </Box>
                    <Box className="upload-actions">
                        <Typography className="upload-label">
                            Logo ou Image de Référence
                        </Typography>
                        <Typography className="upload-hint">
                            Format JPG, PNG (Max 2Mo). Cette image apparaîtra sur les documents.
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="logo-upload-input"
                                type="file"
                                onChange={handleLogoChange}
                            />
                            <label htmlFor="logo-upload-input">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    color="secondary"
                                    startIcon={<PhotoCamera />}
                                    size="small"
                                >
                                    Choisir un fichier
                                </Button>
                            </label>
                            {logoPreview && (
                                <IconButton color="error" onClick={removeLogo} size="small">
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Stack>
                    </Box>
                </Box>
            </Box>

            {/* Actions du formulaire */}
            <Box className="form-actions-cyber">
                <Button
                    variant="text"
                    className="cyber-btn btn-cancel"
                    onClick={onCancel}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    className="cyber-btn btn-submit"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? null : (id ? <Badge /> : <CloudUpload />)}
                >
                    {loading ? 'Traitement...' : 'Enregistrer'}
                </Button>
            </Box>
        </Box>
    );
};

export default ClientForm;
